import React from 'react';
import { View, Text, Platform, StyleSheet } from 'react-native';

export interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

interface Props {
  segments: DonutSegment[];
  size?: number;
  centerValue?: string;
  centerLabel?: string;
  showLegend?: boolean;
}

function toRad(deg: number) { return (deg * Math.PI) / 180; }

export default function MiniDonutChart({
  segments, size = 110, centerValue, centerLabel, showLegend = true,
}: Props) {
  if (!Array.isArray(segments) || !segments.length) {
    return (
      <View style={styles.fallback}>
        <Text style={styles.fallbackVal}>{centerValue ?? '—'}</Text>
        {centerLabel ? <Text style={styles.fallbackLabel}>{centerLabel}</Text> : null}
      </View>
    );
  }
  const total = segments.reduce((s, seg) => s + seg.value, 0);

  if (Platform.OS !== 'web' || !total) {
    return (
      <View style={styles.fallback}>
        <Text style={styles.fallbackVal}>{centerValue ?? `${Math.round((segments[0]?.value ?? 0) / (total || 1) * 100)}%`}</Text>
        {centerLabel ? <Text style={styles.fallbackLabel}>{centerLabel}</Text> : null}
      </View>
    );
  }

  const cx = size / 2;
  const cy = size / 2;
  const R  = size * 0.36;
  const SW = size * 0.14;  // ring thickness

  let angle = -90;
  const arcs = segments.map(seg => {
    const span = (seg.value / total) * 360;
    const start = angle;
    const end   = angle + span;
    angle = end;
    const s = { x: cx + R * Math.cos(toRad(start)), y: cy + R * Math.sin(toRad(start)) };
    const e = { x: cx + R * Math.cos(toRad(end - 0.01)), y: cy + R * Math.sin(toRad(end - 0.01)) };
    const large = span > 180 ? 1 : 0;
    return { ...seg, path: `M ${s.x} ${s.y} A ${R} ${R} 0 ${large} 1 ${e.x} ${e.y}` };
  });

  return (
    <View style={{ alignItems: 'center' }}>
      {/* @ts-ignore */}
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {arcs.map((arc, i) => (
          // @ts-ignore
          <path key={i} d={arc.path} fill="none" stroke={arc.color} strokeWidth={SW} />
        ))}
        {centerValue ? (
          // @ts-ignore
          <text x={cx} y={cy + size * 0.07} textAnchor="middle" fontSize={size * 0.18} fontWeight="800" fill="#111">
            {centerValue}
          </text>
        ) : null}
        {centerLabel ? (
          // @ts-ignore
          <text x={cx} y={cy + size * 0.07 + size * 0.13} textAnchor="middle" fontSize={size * 0.1} fill="#888">
            {centerLabel}
          </text>
        ) : null}
      </svg>
      {showLegend && (
        <View style={styles.legend}>
          {segments.map((s, i) => (
            <View key={i} style={styles.legendItem}>
              <View style={[styles.dot, { backgroundColor: s.color }]} />
              <Text style={styles.legendText}>
                {s.label} {Math.round((s.value / total) * 100)}%
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  fallback: { alignItems: 'center', paddingVertical: 8 },
  fallbackVal: { fontSize: 22, fontWeight: '900', color: '#111' },
  fallbackLabel: { fontSize: 11, color: '#888', marginTop: 2 },
  legend: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 6 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  legendText: { fontSize: 10, color: '#666' },
});
